import { Link } from "react-router-dom";
import { Phone, MessageCircle, Globe, AlertTriangle, Heart, Clock } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from '../components/SEO';

const HOTLINES = [
  {
    name: "Nummer gegen Kummer",
    number: "116 111",
    description: "Kostenlos, anonym, Mo-Sa 14-20 Uhr. Für Kinder und Jugendliche.",
    color: "#A0E8AF",
    website: "https://www.nummergegenkummer.de",
    icon: Phone,
  },
  {
    name: "Telefonseelsorge",
    number: "0800 111 0 111",
    description: "Kostenlos, anonym, 24 Stunden, 7 Tage die Woche.",
    color: "#BDE0FE",
    website: "https://www.telefonseelsorge.de",
    icon: Heart,
  },
  {
    name: "Polizei",
    number: "110",
    description: "Bei akuter Gefahr oder Straftaten. 24 Stunden erreichbar.",
    color: "#FC8181",
    website: null,
    icon: AlertTriangle,
  },
  {
    name: "Elterntelefon",
    number: "0800 111 0 550",
    description: "Kostenlos, anonym, für Eltern. Mo-Fr 9-17 Uhr, Di+Do bis 19 Uhr.",
    color: "#FFC4D6",
    website: "https://www.nummergegenkummer.de/elterntelefon",
    icon: Phone,
  },
];

const ONLINE_HELP = [
  {
    name: "juuuport.de",
    description: "Online-Beratung von jungen Leuten für junge Leute bei Cybermobbing.",
    url: "https://www.juuuport.de",
    icon: MessageCircle,
  },
  {
    name: "klicksafe.de",
    description: "Tipps und Infos für sicheres Surfen im Internet.",
    url: "https://www.klicksafe.de",
    icon: Globe,
  },
  {
    name: "Cybermobbing-Hilfe",
    description: "Erste Hilfe bei Cybermobbing mit konkreten Tipps.",
    url: "https://www.cybermobbing-hilfe.de",
    icon: Heart,
  },
];

export default function Notfall() {
  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Header />
      <SEO title="Notfall-Hotlines bei Mobbing & Cybermobbing" description="Sofortige Hilfe: Nummer gegen Kummer 116 111, Telefonseelsorge 0800 111 0 111. Kostenlose Notfall-Hotlines und Online-Beratung fuer Kinder und Jugendliche." path="/notfall" />
      
      <main className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#FC8181]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Phone className="w-10 h-10 text-[#E53E3E]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>
              Notfall-Hotlines
            </h1>
            <p className="text-lg text-[#718096] max-w-2xl mx-auto">
              Hier findest du Telefonnummern, wo du <strong>sofort</strong> mit jemandem sprechen kannst. 
              Alle sind kostenlos und du bleibst anonym.
            </p>
          </div>

          {/* Emergency Alert */}
          <div className="bg-[#FC8181]/10 border-2 border-[#FC8181] rounded-2xl p-6 mb-10">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-[#E53E3E] flex-shrink-0" />
              <div>
                <h3 className="text-xl font-bold text-[#E53E3E] mb-2">Akute Gefahr?</h3>
                <p className="text-[#718096] mb-3">
                  Wenn du oder jemand anders in <strong>akuter Gefahr</strong> ist, rufe sofort die Polizei an!
                </p>
                <a href="tel:110" className="inline-flex items-center gap-2 bg-[#E53E3E] text-white font-bold px-6 py-3 rounded-full hover:bg-[#C53030] transition-all">
                  <Phone className="w-5 h-5" />
                  110 anrufen
                </a>
              </div>
            </div>
          </div>

          {/* Hotlines Grid */}
          <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
            📞 Telefonische Hilfe
          </h2>
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {HOTLINES.map((hotline, index) => {
              const Icon = hotline.icon;
              return (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-soft border border-gray-100 hover:shadow-hover transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${hotline.color}30` }}
                    >
                      <Icon className="w-6 h-6" style={{ color: hotline.color }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-[#2D3748] mb-1">{hotline.name}</h3>
                      <a 
                        href={`tel:${hotline.number.replace(/\s/g, '')}`}
                        className="text-2xl font-bold mb-2 block hover:underline"
                        style={{ color: hotline.color }}
                      >
                        {hotline.number}
                      </a>
                      <p className="text-sm text-[#718096] mb-3">{hotline.description}</p>
                      {hotline.website && (
                        <a 
                          href={hotline.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-[#718096] hover:text-[#2D3748] underline"
                        >
                          Mehr Infos →
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Online Help */}
          <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
            💻 Online-Hilfe
          </h2>
          <div className="space-y-4 mb-12">
            {ONLINE_HELP.map((help, index) => {
              const Icon = help.icon;
              return (
                <a 
                  key={index}
                  href={help.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 bg-white rounded-xl p-5 shadow-soft border border-gray-100 hover:shadow-hover hover:-translate-y-1 transition-all"
                >
                  <div className="w-12 h-12 bg-[#BDE0FE]/30 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-[#1A365D]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-[#2D3748]">{help.name}</h3>
                    <p className="text-sm text-[#718096]">{help.description}</p>
                  </div>
                  <Globe className="w-5 h-5 text-[#A0AEC0]" />
                </a>
              );
            })}
          </div>

          {/* Reminder */}
          <div className="bg-[#A0E8AF]/10 rounded-2xl p-6 text-center">
            <Clock className="w-8 h-8 text-[#1A4731] mx-auto mb-3" />
            <h3 className="font-bold text-[#1A4731] mb-2">Du bist nicht allein!</h3>
            <p className="text-[#718096]">
              Es ist okay, um Hilfe zu bitten. Die Menschen an diesen Hotlines sind da, um dir zu helfen - 
              nicht um dich zu verurteilen.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
